import { AnimatePresence, motion } from "motion/react";
import React, { useCallback, useEffect, useMemo } from "react";
import { Reference } from "../../adapters/insights-agent/state/AppState.ts";
import { ArtificialIntelligenceIcon } from "../../ui-library/_icons/ArtificialIntelligenceIcon.tsx";
import { Button } from "../../ui-library/Button/Button.tsx";
import { Checkbox } from "../../ui-library/Checkbox/Checkbox.tsx";
import { LoadingDots } from "../../ui-library/LoadingDots/LoadingDots.tsx";
import { SystemWarning } from "../../ui-library/SystemWarning/SystemWarning.tsx";
import { ReferenceBox } from "./ReferenceBox.tsx";
import styles from "./ReferenceSidebar.module.css";
import { TrashIcon } from "./TrashIcon.tsx";

export const ReferenceSidebar: React.FC<{
  references: Pick<Reference, "id" | "title" | "sourceUrl">[];
  pendingSummary?: boolean;
  onRemoveReferences: (referenceIds: string[]) => Promise<void>;
  researchId?: string;
  featureFlags?: { enableRemoveFiles?: boolean };
}> = ({
  references,
  pendingSummary,
  onRemoveReferences,
  researchId,
  featureFlags,
}) => {
  const [checkedReferences, setCheckedReferences] = React.useState<Set<string>>(
    new Set(),
  );
  const [loading, setLoading] = React.useState(false);
  useEffect(() => {
    setCheckedReferences(new Set());
  }, [references]);
  const referenceCheckHandler = useCallback(
    (value: boolean, id: string) => {
      const newCheckedReferences = new Set(checkedReferences);
      if (value) {
        newCheckedReferences.add(id);
      } else {
        newCheckedReferences.delete(id);
      }
      setCheckedReferences(newCheckedReferences);
    },
    [checkedReferences],
  );
  const selectAllHandler = useCallback(
    (value: boolean) => {
      if (value) {
        setCheckedReferences(
          new Set(references.map((reference) => reference.id)),
        );
      } else {
        setCheckedReferences(new Set());
      }
    },
    [references],
  );
  const selectAllLabel: string = useMemo(() => {
    if (checkedReferences.size) {
      const plural = checkedReferences.size > 1 ? "s" : "";
      return `${checkedReferences.size} file${plural} selected`;
    }
    const plural = references.length > 1 || references.length === 0 ? "s" : "";
    return `${references.length} file${plural}`;
  }, [checkedReferences.size, references.length]);
  const helperText = useMemo(() => {
    const text = "(select files to remove)";
    if (checkedReferences.size === 0) {
      return <em className={styles.SelectFilesToRemoveHelperText}>{text}</em>;
    }
  }, [checkedReferences.size]);
  if (pendingSummary) {
    return (
      <article
        role={"complementary"}
        className={styles.ReferenceSidebar + " " + styles.Loading}
      >
        Once this summary is done, your references will go here.
      </article>
    );
  }
  return (
    <article role={"complementary"} className={styles.ReferenceSidebar}>
      <SystemWarning icon={<ArtificialIntelligenceIcon />}>
        AI-generated answers can be a great starting point, but always
        double-check the details. <br />
        Verify information through the linked citations below to ensure
        accuracy.
      </SystemWarning>
      <h2 className={styles.Title}>References</h2>
      {featureFlags?.enableRemoveFiles && (
        <div className={styles.ActionBar}>
          <Checkbox
            onChange={selectAllHandler}
            checked={
              !!checkedReferences.size &&
              references.length === checkedReferences.size
            }
            disabled={references.length === 0}
          >
            {selectAllLabel} {helperText}
          </Checkbox>
          {checkedReferences.size > 0 && (
            <Button
              onClick={() => {
                setLoading(true);
                const ok = window.confirm(
                  "This will completely remove the selected files from the search. Are you sure you want to proceed?",
                );
                if (!ok) {
                  setLoading(false);
                  return;
                }
                onRemoveReferences(Array.from(checkedReferences)).then(() => {
                  setLoading(false);
                });
              }}
              disabled={!checkedReferences.size || loading}
              variant={"transparent"}
              className={styles.ActionButton}
            >
              <TrashIcon /> Remov{loading ? "ing" : "e"} file
              {checkedReferences.size > 1 ? "s" : ""} from search
              {loading && <LoadingDots />}
            </Button>
          )}
        </div>
      )}
      <ul className={styles.ReferenceList} key={researchId}>
        <AnimatePresence>
          {references.map((reference) => (
            <motion.li
              key={reference.id}
              layout
              exit={{
                translateX: "100%",
                opacity: 0,
                height: 0,
                margin: 0,
                padding: 0,
                border: 0,
                overflow: "hidden",
              }}
            >
              <ReferenceBox
                reference={reference}
                onCheck={
                  featureFlags?.enableRemoveFiles
                    ? referenceCheckHandler
                    : undefined
                }
                checked={checkedReferences.has(reference.id)}
              />
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
    </article>
  );
};
